import type { JSX } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { AlertTriangle, ClipboardList, Globe, Package, ScrollText, Users } from "lucide-react";
import { AppShell, Toaster, type NavGroup } from "@nahal/ui";
import { SearchScreen } from "../features/search/SearchScreen";
import { CustomerScreen } from "../features/customer/CustomerScreen";
import { QueueScreen } from "../features/queue/QueueScreen";
import { AuditScreen } from "../features/audit/AuditScreen";
import { CatalogScreen } from "../features/catalog/CatalogScreen";
import { IncidentInboxScreen } from "../features/incidents/IncidentInboxScreen";
import { DomainQueueScreen } from "../features/domains/DomainQueueScreen";
import { useQueueSummary } from "../lib/queries";

const activeKeyFor = (pathname: string) => {
  if (pathname.startsWith("/queue")) return "queue";
  if (pathname.startsWith("/incidents")) return "incidents";
  if (pathname.startsWith("/catalog")) return "catalog";
  if (pathname.startsWith("/domains")) return "domains";
  if (pathname.startsWith("/audit")) return "audit";
  return "customers";
};

function Shell() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const queue = useQueueSummary();

  const nav: NavGroup[] = [
    {
      key: "support",
      label: "Support",
      items: [
        { key: "customers", label: "Customers", href: "/", icon: <Users className="size-5" /> },
        {
          key: "queue",
          label: "Queue",
          href: "/queue",
          icon: <ClipboardList className="size-5" />,
          badge: queue.data?.pending ? queue.data.pending : undefined,
        },
        {
          key: "incidents",
          label: "Incidents",
          href: "/incidents",
          icon: <AlertTriangle className="size-5" />,
        },
        {
          key: "catalog",
          label: "Catalog",
          href: "/catalog",
          icon: <Package className="size-5" />,
        },
        { key: "domains", label: "Domains", href: "/domains", icon: <Globe className="size-5" /> },
        { key: "audit", label: "Audit", href: "/audit", icon: <ScrollText className="size-5" /> },
      ],
    },
  ];

  return (
    <>
      <AppShell
        surface="ops"
        productName="Nahal Labs Ops"
        nav={nav}
        activeKey={activeKeyFor(pathname)}
        onNavigate={(href) => navigate({ to: href })}
        // Catalog is reference reading, not a support destination -- on a phone the
        // four that matter are who, what is waiting, what is broken, and what changed.
        primaryNavKeys={["customers", "queue", "incidents", "audit"]}
      >
        <Outlet />
      </AppShell>
      <Toaster />
    </>
  );
}

const rootRoute = createRootRoute({ component: Shell });

const screen = (path: string, component: () => JSX.Element) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

const routeTree = rootRoute.addChildren([
  screen("/", SearchScreen),
  screen("/queue", QueueScreen),
  screen("/incidents", IncidentInboxScreen),
  screen("/catalog", CatalogScreen),
  screen("/domains", DomainQueueScreen),
  screen("/audit", AuditScreen),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/customers/$organizationId",
    component: CustomerScreen,
  }),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
